import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import Dropdown from "../../components/DropDown";
import InputWithLabel from "../InputWithLabel";

import {
  businessTypeOptions,
  xflowCountryOptions,
} from "../../constants/xFlow";
import { add_xflow_partner, update_xflow_partner } from "../../constants/apis";
import { useAppContext } from "../../context/AppContext";
import { toast } from "react-toastify";
import { useXFlowPartnerContext } from "../../context/XFlowPartners";
import { useSearchParams } from "react-router-dom";

const physicalAddressSchema = z.object({
  city: z.string().trim().min(1, "City is required"),
  country: z.string().trim().min(1, "Country is required"),
  line1: z.string().trim().min(1, "Address line 1 is required"),
  postal_code: z.string().trim().min(1, "Postal code is required"),
  state: z.string().trim().min(1, "State is required"),
});

const businessDetailsSchema = z.object({
  email: z
    .string()
    .email("Invalid email address")
    .trim()
    .min(1, "Email is required"),
  legal_name: z.string().trim().min(1, "Legal name is required"),
  physical_address: physicalAddressSchema,
  b_type: z.string().trim().min(1, "Business type is required"),
});

const partnerSchema = z.object({
  id: z.string().optional(),
  business_details: businessDetailsSchema,
  nickname: z.string().trim().min(1, "Nickname is required"),
  type: z.string().default("partner"),
});

type PartnerType = z.infer<typeof partnerSchema>;

const initialValues = {
  id: "",
  business_details: {
    email: "",
    legal_name: "",
    b_type: "",
    physical_address: {
      line1: "",
      city: "",
      country: "",
      state: "",
      postal_code: "",
    },
  },
  nickname: "",
  type: "partner",
};

export default function AddPartnerForm({
  toggleAddPartnerModal,
}: {
  toggleAddPartnerModal: () => void;
}) {
  const { jwtToken } = useAppContext();
  const { refetch, data: allPartners } = useXFlowPartnerContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const partnerId = searchParams.get("partner_id");

  const filteredPartner = allPartners?.find(
    (partner) => partner.id === partnerId
  );

  // console.log(filteredPartner);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<PartnerType>({
    resolver: zodResolver(partnerSchema),
    values:
      partnerId && filteredPartner
        ? {
            ...filteredPartner,
            business_details: {
              ...filteredPartner?.business_details,

              b_type: filteredPartner?.business_details.type,
            },
          }
        : initialValues,
  });

  //   console.log(errors);

  const onSubmitHandler: SubmitHandler<PartnerType> = async (values) => {
    // console.log(values);
    // return;

    const payload = {
      partner_id: partnerId || "",
      email: values.business_details.email,
      name: values.business_details.legal_name,
      city: values.business_details.physical_address.city,
      country: values.business_details.physical_address.country,
      address: values.business_details.physical_address.line1,
      postal_code: values.business_details.physical_address.postal_code,
      state: values.business_details.physical_address.state,
      type: values.business_details.b_type,
      nickname: values.nickname,
    };

    const url = partnerId ? update_xflow_partner : add_xflow_partner;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Content_Type: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.status === "success") {
        if (data?.xflow_response?.http_status_code === 400) {
          toast.error("Partner creation failed");
        }

        if (partnerId) {
          setSearchParams({});
        }

        toast.success("Successfully saved");
        reset();
        refetch();
        toggleAddPartnerModal();
      }
    } catch (error) {
      console.log(error);
      toast.error("something went wrong");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <div className="mb-4">
        <h2 className="font-semibold text-2xl">Partner Company Details</h2>
        <p className="text-[#344054] text-sm">
          Add a partner on behalf of the user
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {/* Nickname */}
        <div className="col-span-full">
          <InputWithLabel
            label="Nickname"
            placeholder="Enter here"
            {...register("nickname")}
            error={errors.nickname?.message}
            disabled={partnerId ? true : false}
          />
        </div>

        {/* Type */}
        {/* <InputWithLabel
        label="Type"
        placeholder="Enter 'partner'"
        {...register("type")}
        error={errors.type?.message}
      /> */}

        <hr className="col-span-full" />

        <h2 className="col-span-full font-semibold text-[#344054]">
          Business Details
        </h2>

        {/* Legal Name */}
        <div className="col-span-full">
          <InputWithLabel
            label="Legal company Name"
            placeholder="Enter legal company name"
            {...register("business_details.legal_name")}
            error={errors.business_details?.legal_name?.message}
            disabled={partnerId ? true : false}
          />
        </div>

        {/* Email */}
        <InputWithLabel
          label="Email"
          placeholder="Enter business email"
          {...register("business_details.email")}
          error={errors.business_details?.email?.message}
        />

        {/* Business Type */}
        <Dropdown
          label="Business Type"
          placeholder="Select business type"
          options={businessTypeOptions}
          {...register("business_details.b_type")}
          error={errors.business_details?.b_type?.message}
          disabled={partnerId ? true : false}
        />

        <hr className="col-span-full" />

        <h2 className="col-span-full font-semibold text-[#344054]">
          Physical Address
        </h2>

        {/* Line 1 */}
        <InputWithLabel
          label="Address Line 1"
          placeholder="Enter here"
          {...register("business_details.physical_address.line1")}
          error={errors.business_details?.physical_address?.line1?.message}
        />

        {/* City */}
        <InputWithLabel
          label="City"
          placeholder="Enter here"
          {...register("business_details.physical_address.city")}
          error={errors.business_details?.physical_address?.city?.message}
        />

        {/* State */}
        <InputWithLabel
          label="State"
          placeholder="Enter here"
          {...register("business_details.physical_address.state")}
          error={errors.business_details?.physical_address?.state?.message}
        />

        {/* Postal Code */}
        <InputWithLabel
          label="Postal Code"
          placeholder="Enter here"
          {...register("business_details.physical_address.postal_code")}
          error={
            errors.business_details?.physical_address?.postal_code?.message
          }
        />

        {/* Country */}

        <Dropdown
          label="Country"
          placeholder="Select country"
          options={xflowCountryOptions}
          {...register("business_details.physical_address.country")}
          error={errors.business_details?.physical_address?.country?.message}
        />
      </div>

      <div className="col-span-full text-end mt-4 border-t pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-[146px] h-[44px] rounded-md bg-custom-blue-medium text-white font-medium"
        >
          {isSubmitting ? "submitting.." : "Save"}
        </button>
      </div>
    </form>
  );
}
